import endpoints from "../api/endpoints";
import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { BUCKET_DEVICES } from "../Constants";
import DataGridTable from "../Components/DataGridTable";
import axios from "axios";
import dayjs from "dayjs";
import { useAuth } from "../hooks/AuthProvider";
import { useSearchParams } from "react-router-dom";

import { LanguageContext } from "../context/LanguageContext";
import notifTranslations from "../languages/notification.json";
import Alert from '../Components/Alert';
import './Notifications.css';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { GridActionsCellItem } from '@mui/x-data-grid';
import NotificationDetailsModal from "../Components/NotificationDetailsModal";

export const Notifications = () => {

	const { org, permissions } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const canViewNotifications = permissions['view_notification'];
	const canDeleteNotifications = permissions['delete_device_data'];

	const { lang } = useContext(LanguageContext);
	const tPage = notifTranslations[lang].notifications;
	const isRtl = ['ar', 'ur'].includes(lang);
	const [viewingNotification, setViewingNotification] = useState({
		open: false,
		data: null
	});
	const initialRange = {
		tstart: dayjs().subtract(1, 'day').unix(),
		tstop: dayjs().unix(),
		inputValue: 1,
		timeUnit: 'day'
	};
	const [data, setData] = useState({ rows: [] });
	const rows = data.rows;
	const estimateColumnWidth = useCallback((field, headerName, options = {}) => {
		const {
			minWidth = 140,
			maxWidth = 720,
			padding = 64,
			charWidth = 8.5,
			formatValue = (value) => value
		} = options;
		const headerText = String(headerName || field || "");
		const longestText = rows.reduce((longest, row) => {
			const rawValue = row?.[field];
			const value = formatValue(rawValue, row);
			const text = String(value ?? "");
			return text.length > longest.length ? text : longest;
		}, headerText);
		const estimatedWidth = Math.ceil(longestText.length * charWidth + padding);

		return Math.max(minWidth, Math.min(maxWidth, estimatedWidth));
	}, [rows]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [rowCount, setRowCount] = useState(0);
	const [filterModel, setFilterModel] = useState({ items: [] });
	const [rangeVersion, setRangeVersion] = useState(0);
	const timeRange = useRef(initialRange);
	const [dateRange, setDateRange] = useState({
		from: dayjs().subtract(1, 'day').format("YYYY-MM-DDTHH:mm"),
		to: dayjs().format("YYYY-MM-DDTHH:mm")
	});
	const [alertState, setAlertState] = useState({
		open: false,
		message: '',
		type: 'info',
		position: isRtl ? 'left' : 'right'
	});

	const showAlert = useCallback((type, message) => {
		setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
	}, [isRtl]);

	const mapNotificationRows = useCallback((items) => {
		return (Array.isArray(items) ? items : []).map((r, idx) => ({
			rowId: `${r._time || "t"}-${r.id || "n"}-${idx}`,
			_time: new Date(r._time),
			sourceDid: r?.source_did ?? r?.sourceDevice ?? r?.did ?? "",
			device: r?.source_did ?? r?.sourceDevice ?? r?.did ?? "",
			deviceId:
				r?.source_device?.id ??
				r?.automation?.device?.id ??
				null,
			deviceName:
				r?.source_device_name ??
				r?.source_device?.name ??
				r?.automation?.device?.name ??
				"",
			template:
				r?.source_device?.template?.name ??
				r?.automation?.template?.name ??
				r?.automation?.tid ??
				"",
			organization:
				r?.source_device?.organization?.name ??
				r?.source_device?.organization_name ??
				r?.automation?.organization?.name ??
				r?.automation?.organization_name ??
				r?.automation?.device?.organization?.name ??
				r?.automation?.device?.organization_name ??
				"",
			message: r.message ?? r.description ?? "",
			notificationType: r.notificationType ?? "",
			source: r.trigger ?? r.source ?? "",
			raw: r
		}));
	}, []);

	const applyNotificationPayload = useCallback((items) => {
		setData({ rows: mapNotificationRows(items) });
	}, [mapNotificationRows]);

	const resolvePaginationTotal = useCallback((pagination) => {
		const parsedTotal = Number(pagination?.total);

		if (pagination?.total !== undefined && Number.isFinite(parsedTotal) && parsedTotal >= 0) {
			return parsedTotal;
		}

		return null;
	}, []);

	const streamRef = useRef(null);
	const retryTimeoutRef = useRef(null);
	const hasReceivedPayloadRef = useRef(false);
	const retryDelayRef = useRef(3000);
	const streamRequestKeyRef = useRef("");

	const buildNotificationQueryParams = useCallback(() => ({
		bucket: BUCKET_DEVICES,
		tstart: timeRange.current.tstart,
		tstop: timeRange.current.tstop,
		page,
		pageSize,
		includeTotal: page === 1,
		filters: JSON.stringify(filterModel)
	}), [filterModel, page, pageSize]);

	const buildNotificationStreamKey = useCallback(() => {
		return [
			org?.id || "",
			BUCKET_DEVICES,
			timeRange.current.tstart,
			timeRange.current.tstop,
			page,
			pageSize,
			JSON.stringify(filterModel),
			rangeVersion
		].join("|");
	}, [filterModel, org?.id, page, pageSize, rangeVersion]);

	const closeNotificationStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.close();
			streamRef.current = null;
		}

		if (retryTimeoutRef.current) {
			clearTimeout(retryTimeoutRef.current);
			retryTimeoutRef.current = null;
		}
	}, []);

	const fetchData = useCallback(() => {
		if (!canViewNotifications) {
			closeNotificationStream();
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
			setData({ rows: [] });
			setRowCount(0);
			setLoading(false);
			return;
		}

		if (!org?.id) {
			closeNotificationStream();
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
			setData({ rows: [] });
			setRowCount(0);
			setLoading(false);
			return;
		}

		const queryParams = buildNotificationQueryParams();
		const streamRequestKey = buildNotificationStreamKey();
		streamRequestKeyRef.current = streamRequestKey;
		setLoading(true);
		closeNotificationStream();

		axios.get(endpoints.data.notifications(org.id, queryParams))
			.then(({ data: res }) => {
				if (streamRequestKeyRef.current !== streamRequestKey) return;
				const items = Array.isArray(res?.data) ? res.data : [];
				applyNotificationPayload(items);
				const total = resolvePaginationTotal(res?.pagination);
				if (total !== null) setRowCount(total);
			})
			.catch((err) => {
				if (streamRequestKeyRef.current !== streamRequestKey) return;
				console.error("Failed to load notifications:", err);
				showAlert('error', err?.response?.data?.message || 'Failed to load notifications.');
			})
			.finally(() => {
				if (streamRequestKeyRef.current === streamRequestKey) setLoading(false);
			});
	}, [
		applyNotificationPayload,
		buildNotificationStreamKey,
		buildNotificationQueryParams,
		canViewNotifications,
		closeNotificationStream,
		org?.id,
		resolvePaginationTotal,
		showAlert
	]);

	const applyDateRange = useCallback(() => {
		const tstart = dayjs(dateRange.from).unix();
		const tstop = dayjs(dateRange.to).unix();

		timeRange.current = { tstart, tstop };
		setPage(1);
		setRangeVersion((value) => value + 1);
	}, [dateRange]);

	const refreshNotifications = useCallback(() => {
		setRangeVersion((value) => value + 1);
	}, []);

	const openNotificationDetailsFromRaw = useCallback((rawNotification) => {
		const mappedRow = mapNotificationRows([rawNotification])[0];
		if (!mappedRow) return;

		setViewingNotification({
			open: true,
			data: mappedRow
		});
	}, [mapNotificationRows]);

	const handleDeleteNotification = useCallback(async (row) => {
		if (!canDeleteNotifications || !org?.id) return;

		const raw = row?.raw || {};
		const measurement = raw._measurement;
		const time = raw._time || row?._time;

		if (!measurement || !time) {
			showAlert('error', 'Cannot delete this notification because its identity is incomplete.');
			return;
		}

		const confirmed = window.confirm('Delete this automation notification from Influx? This cannot be undone.');
		if (!confirmed) return;

		try {
			await axios.delete(endpoints.data.deleteNotification(org.id), {
				data: {
					bucket: BUCKET_DEVICES,
					time,
					measurement,
					automation_id: raw.id,
					source_did: raw.source_did || raw.sourceDevice || raw.did || row?.sourceDid || undefined
				}
			});
			showAlert('success', 'Notification deleted.');
			setPage(1);
			refreshNotifications();
		} catch (err) {
			showAlert('error', err?.response?.data?.message || err?.message || 'Failed to delete notification.');
		}
	}, [canDeleteNotifications, org?.id, refreshNotifications, showAlert]);

	const handleClearNotifications = useCallback(async () => {
		if (!canDeleteNotifications || !org?.id) return;

		const confirmed = window.confirm(
			'Clear all automation notifications for this organization scope from Influx? This cannot be undone.'
		);
		if (!confirmed) return;

		try {
			await axios.delete(endpoints.data.clearNotifications(org.id), {
				data: {
					bucket: BUCKET_DEVICES,
					confirm: 'CLEAR_AUTOMATION_NOTIFICATIONS'
				}
			});
			showAlert('success', 'Automation notifications cleared.');
			setPage(1);
			refreshNotifications();
		} catch (err) {
			showAlert('error', err?.response?.data?.message || err?.message || 'Failed to clear notifications.');
		}
	}, [canDeleteNotifications, org?.id, refreshNotifications, showAlert]);

	useEffect(() => {
		setPage(1);
	}, [org?.id]);

	useEffect(() => {
		setPage(1);
		setRangeVersion((value) => value + 1);
	}, [filterModel]);

	useEffect(() => {
		const notificationId = searchParams.get("notificationId");
		if (!notificationId || !org?.id || !canViewNotifications) return;

		let active = true;
		const loadNotificationDetails = async () => {
			try {
				const res = await axios.get(
					endpoints.data.notificationDetails(org.id, notificationId, { bucket: BUCKET_DEVICES })
				);
				if (!active) return;
				openNotificationDetailsFromRaw(res?.data?.data);
			} catch (err) {
				if (!active) return;
				showAlert('error', err?.response?.data?.message || err?.message || 'Failed to open notification details.');
			} finally {
				if (active) {
					const nextParams = new URLSearchParams(searchParams);
					nextParams.delete("notificationId");
					setSearchParams(nextParams, { replace: true });
				}
			}
		};

		loadNotificationDetails();

		return () => {
			active = false;
		};
	}, [
		canViewNotifications,
		openNotificationDetailsFromRaw,
		org?.id,
		searchParams,
		setSearchParams,
		showAlert
	]);

	useEffect(() => {
		fetchData();
	}, [fetchData, rangeVersion]);

	useEffect(() => {
		return () => {
			closeNotificationStream();
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
		};
	}, [closeNotificationStream]);

	const columns = useMemo(() => [
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem
					icon={<VisibilityOutlinedIcon />}
					label="View"
					showInMenu
					onClick={() => {
						setViewingNotification({
							open: true,
							data: params.row
						});
					}}
				/>,
				...(canDeleteNotifications ? [
					<GridActionsCellItem
						icon={<DeleteOutlineOutlinedIcon />}
						label="Delete"
						showInMenu
						onClick={() => handleDeleteNotification(params.row)}
					/>
				] : [])
			]
		},
		{
			field: '_time',
			headerName: tPage.columns.time,
			type: 'dateTime',
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('_time', tPage.columns.time, {
				minWidth: 200,
				maxWidth: 240,
				formatValue: (value) => value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : ""
			}),
		},

		{
			field: 'deviceName',
			headerName: tPage.columns.deviceName || 'Device Name',
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('deviceName', tPage.columns.deviceName || 'Device Name', {
				minWidth: 180,
			}),
		},
		{
			field: 'organization',
			headerName: tPage.columns.organization,
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('organization', tPage.columns.organization, {
				minWidth: 180,
			}),
		},
		{
			field: 'template',
			headerName: tPage.columns.template || 'Template',
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('template', tPage.columns.template || 'Template', {
				minWidth: 160,
			}),
		},

		{
			field: 'source',
			headerName: tPage.columns.source,
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('source', tPage.columns.source, {
				minWidth: 150,
			}),
		},
		{
			field: 'notificationType',
			headerName: tPage.columns.notificationType,
			headerAlign: 'center',
			align: "center",
			width: estimateColumnWidth('notificationType', tPage.columns.notificationType, {
				minWidth: 180,
			}),
		},
	], [
		canDeleteNotifications,
		estimateColumnWidth,
		handleDeleteNotification,
		tPage.columns.deviceName,
		tPage.columns.notificationType,
		tPage.columns.organization,
		tPage.columns.source,
		tPage.columns.template,
		tPage.columns.time
	]);
	const pagingoptions = [10, 20, 50, 100];
	const idcolumn = 'rowId';
	const sortField = '_time';
	const sortType = 'desc';

	if (!canViewNotifications) {
		return (
			<div className="grid-container notif-permission-denied">
				<h2>Permission Denied</h2>
				<p>You do not have the required permission to view notifications.</p>
			</div>
		);
	}

	return (
		<div className="configuration">
			<Alert
				open={alertState.open}
				close={() => setAlertState({ ...alertState, open: false })}
				message={alertState.message}
				type={alertState.type}
				position={alertState.position}
			/>
			<NotificationDetailsModal
				state={viewingNotification}
				setState={setViewingNotification}
			/>
			<div>
				<div
					className="date-range-container"
					style={{
						display: 'flex',
						gap: '12px',
						alignItems: 'center',
						flexWrap: 'wrap'
					}}
				>
					<input
						type="datetime-local"
						value={dateRange.from}
						onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
					/>

					<input
						type="datetime-local"
						value={dateRange.to}
						onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
					/>

					<button
						onClick={applyDateRange}
						disabled={loading}
						className="devices-primary-btn"
					>
						Apply
					</button>
					{canDeleteNotifications && (
						<button
							onClick={handleClearNotifications}
							disabled={loading || (!rowCount && rows.length === 0)}
							className="devices-primary-btn notif-danger-btn"
						>
							Clear All
						</button>
					)}
				</div>		</div>
			<div className="devices-grid-wrapper">
				<DataGridTable
					rows={rows}
					columns={columns}
					idcolumn={idcolumn}
					pagingoptions={pagingoptions}
					sortField={sortField}
					sortType={sortType}
					showToolbar={true}
					tableKey='notifications'
					loading={loading}
					pagination={{
						serverSide: true,
						page,
						pageSize,
						count: rowCount,
						onFilterModelChange: (newFilterModel) => {
							setFilterModel(newFilterModel || { items: [] });
						},
						onPageChange: (newPage) => setPage(newPage),
						onPageSizeChange: (newSize) => {
							setPageSize(newSize);
							setPage(1);
						}
					}}
					handleRowClick={(params) => {
						setViewingNotification({
							open: true,
							data: params.row
						});
					}}
				/>
			</div>
		</div>
	);
};
